import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardBody } from '@heroui/react';
import { CheckCircle, GraduationCap, BookOpen } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useUserMetrics } from '@/hooks/useGuides';

const MetricsCards: React.FC = () => {
  const { t } = useTranslation('home');
  const { currentLanguage } = useLanguage();
  const language = currentLanguage || 'en';

  const { data: metricsData, isLoading } = useUserMetrics(language);

  const completedCount = metricsData?.data?.completedCount || 0;
  const completedCoursesCount = metricsData?.data?.completedCoursesCount || 0;
  const enrolledCoursesCount = metricsData?.data?.enrolledCoursesCount || 0;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="h-32">
            <CardBody className="animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </CardBody>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Completed Guides */}
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
        <Card className="h-full bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border border-emerald-200 dark:border-emerald-800">
          <CardBody className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2">
                  {t('metrics.completedGuides')}
                </p>
                <p className="text-4xl font-bold text-emerald-900 dark:text-emerald-100">{completedCount}</p>
              </div>
              <div className="p-4 rounded-full bg-emerald-100 dark:bg-emerald-900/50">
                <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardBody>
        </Card>
      </motion.div>

      {/* Completed Courses */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card className="h-full bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border border-purple-200 dark:border-purple-800">
          <CardBody className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2">
                  {t('metrics.completedCourses')}
                </p>
                <p className="text-4xl font-bold text-purple-900 dark:text-purple-100">{completedCoursesCount}</p>
              </div>
              <div className="p-4 rounded-full bg-purple-100 dark:bg-purple-900/50">
                <GraduationCap className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardBody>
        </Card>
      </motion.div>

      {/* Enrolled Courses */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card className="h-full bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-800">
          <CardBody className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                  {t('metrics.enrolledCourses')}
                </p>
                <p className="text-4xl font-bold text-blue-900 dark:text-blue-100">{enrolledCoursesCount}</p>
              </div>
              <div className="p-4 rounded-full bg-blue-100 dark:bg-blue-900/50">
                <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardBody>
        </Card>
      </motion.div>
    </div>
  );
};

export default MetricsCards;
